import omit from 'lodash/omit';
import range from 'lodash/range';
import Raven from 'raven-js';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import * as api from '../../api';
import * as analytics from '../../api/analytics';
import { IDocumentObject } from '../../components/agentResaleOfferFlow/DocumentUploadCard';
import { IOfferDetailPageOnSubmitParams } from '../../components/agentResaleOfferFlow/OfferDetailPage';
import {
  IListingOfferParams,
  IListingOfferParamsDocument,
} from '../../declarations/agentResaleOfferPortal';
import { ADMIN_CLIENT_UPLOAD_BUCKET } from '../../helpers/globals';
import * as actions from './actions';
import { AgentResaleOfferPortalActionTypeMap } from './actionTypeMap';
import * as actionTypes from './actionTypes';
import { agentAndListingData, getDocumentUploadFailures } from './selectors';

function generateListingOfferParams(
  listingOfferData,
  cachedData,
  offerDetails,
): IListingOfferParams {
  let listingOfferParams = Object.assign(listingOfferData, cachedData);

  if (!offerDetails.contingentOfferStatus) {
    listingOfferParams = omit(listingOfferParams, 'contingent_offer_status');
  }
  return listingOfferParams;
}

function getFilteredDocumentList(
  documentList: IOfferDetailPageOnSubmitParams['documentList'],
  failedUploads: string[],
  pdfSplittings: IOfferDetailPageOnSubmitParams['pdfSplittings'],
): IOfferDetailPageOnSubmitParams['documentList'] {
  const splitDocumentIds: Array<string | number> = [];
  pdfSplittings.forEach(splitPdf =>
    splitPdf.documentSplittings.forEach(splitting => {
      if (splitting.uniqueId === undefined) {
        return;
      }
      splitDocumentIds.push(splitting.uniqueId);
    }),
  );

  return documentList.filter(
    document =>
      splitDocumentIds.indexOf(document.uniqueId) === -1 &&
      failedUploads.indexOf(document.s3Key) === -1 &&
      document.file,
  );
}

function getUniqueIdToDocumentTypeMap(
  documentList: IOfferDetailPageOnSubmitParams['documentList'],
): { [uniqueId: string]: string } {
  const map: { [uniqueId: string]: string } = {};
  documentList.forEach(document => (map[document.uniqueId] = document.documentType));
  return map;
}

function generateDocumentsParam(
  documentList: IOfferDetailPageOnSubmitParams['documentList'],
  failedUploads: string[],
  pdfSplittings: IOfferDetailPageOnSubmitParams['pdfSplittings'],
): IListingOfferParams['documents'] {
  const documentsParam = getFilteredDocumentList(documentList, failedUploads, pdfSplittings).map(
    (doc: IDocumentObject) => ({
      document_type: doc.documentType,
      file_name: doc.fileName,
      key: doc.s3Key,
    }),
  );

  const uniqueIdToDocumentTypeMap = getUniqueIdToDocumentTypeMap(documentList);

  pdfSplittings.forEach(splitPdf => {
    if (failedUploads.indexOf(splitPdf.s3Key) !== -1 || !splitPdf.pdfFile) {
      return;
    }

    const splitData: IListingOfferParamsDocument['split_data'] = {};
    splitPdf.documentSplittings.forEach(splitting => {
      splitData[splitting.displayName] = {
        // NOTE: server-side is 0-indexed & client is 1-indexed
        document_pages: range(splitting.startPage - 1, splitting.endPage),
        document_type: uniqueIdToDocumentTypeMap[splitting.uniqueId] || 'misc',
      };
    });
    const document: IListingOfferParamsDocument = {
      document_type: 'combined_file',
      file_name: splitPdf.pdfFile.name,
      key: splitPdf.s3Key,
      split_data: splitData,
    };
    documentsParam.push(document);
  });

  return documentsParam;
}

function* getAgent(action: AgentResaleOfferPortalActionTypeMap['GET_AGENT']) {
  const { fullName, phone } = action.payload;
  try {
    const agent = yield call(api.getAgent, fullName, phone);
    analytics.register(agent);
    yield put(actions.setAgent(agent));
  } catch (e) {
    yield put(actions.errorAgent(e.message));
  }
}

function* getListing(action: AgentResaleOfferPortalActionTypeMap['GET_LISTING']) {
  try {
    const listing = yield call(api.getListing, action.payload.listingSlug);
    yield put(actions.setListing(listing));
  } catch (e) {
    yield put(actions.errorListing(e.message));
  }
}

function* submitListingOffer(action: AgentResaleOfferPortalActionTypeMap['SUBMIT_LISTING_OFFER']) {
  const offerDetails = action.payload.offerDetails;
  const failedUploads = yield (select(getDocumentUploadFailures) as any) as string[];
  const documentsParam = generateDocumentsParam(
    offerDetails.documentList,
    failedUploads,
    offerDetails.pdfSplittings,
  );
  const listingOfferData = {
    agent_first_name: offerDetails.typedAgentFirstName,
    agent_last_name: offerDetails.typedAgentLastName,
    agent_license_number: offerDetails.typedAgentMlsLicenseNumber,
    agent_mls_id: offerDetails.typedAgentMlsId,
    agent_office_id: offerDetails.typedAgentMlsOfficeId,
    buyer_note: offerDetails.agentComment,
    close_of_escrow: offerDetails.closeOfEscrow.toString(),
    concession_from_seller_cents: offerDetails.sellerConcession,
    contact_email: offerDetails.typedAgentEmail,
    contingent_offer_status: offerDetails.contingentOfferStatus,
    documents: documentsParam,
    financing_type: offerDetails.financingType,
    interested_in_lower: offerDetails.interestedInLower,
    offered_by_type: offerDetails.offeredByType,
    price_cents: offerDetails.offerPrice,
  };
  const cachedData = yield select(agentAndListingData);
  const listingOfferParams = generateListingOfferParams(listingOfferData, cachedData, offerDetails);
  try {
    const listingOffer = yield call(api.createListingOffer, listingOfferParams);
    yield put(actions.setOfferSubmissionCompleted());
    yield put(actions.setListingOffer(listingOffer));
    analytics.trackEvent('resale-offer-portal', 'submit-offer', 'success', {
      contactEmail: offerDetails.typedAgentEmail,
      slug: listingOfferParams.listing_slug,
    });
  } catch (e) {
    Raven.captureException(e);
    analytics.trackEvent('resale-offer-portal', 'submit-offer', 'error', {
      contactEmail: offerDetails.typedAgentEmail,
      slug: listingOfferParams.listing_slug,
    });
    yield put(actions.errorListingOffer(e.message));
  }
}

function* getDocumentUploadUrl(s3Key: string) {
  try {
    return yield call(api.getS3DocumentUploadUrl, ADMIN_CLIENT_UPLOAD_BUCKET, s3Key);
  } catch (e) {
    yield put(actions.errorOnGetDocumentUploadUrl(e.message, s3Key));
    return { upload_url: '' };
  }
}

function* uploadDocument(action: AgentResaleOfferPortalActionTypeMap['UPLOAD_DOCUMENT']) {
  const {
    bucketName: source,
    document: { file: file, s3Key: s3Key },
    fileTypeId,
  } = action.payload;

  // Disable submit and show loading spinner before calling Web for the upload url.
  yield put(actions.beginDocumentUpload(s3Key, source));

  const { upload_url } = yield getDocumentUploadUrl(s3Key);

  try {
    yield call(api.uploadDocumentToS3, file, upload_url);
    analytics.trackEvent('resale-offer-portal', 'upload', 'upload-success');
    yield put(actions.documentUploadSuccess(s3Key, source, fileTypeId));
  } catch (e) {
    Raven.captureException(e);
    analytics.trackEvent('resale-offer-portal', 'upload', 'upload-error', {
      error: e.message,
      s3Key,
    });
    yield put(actions.errorOnDocumentUpload(s3Key, source, fileTypeId));
  }
}

export default function* saga() {
  yield all([
    takeLatest(actionTypes.GET_AGENT, getAgent),
    takeLatest(actionTypes.GET_LISTING, getListing),
    takeEvery(actionTypes.UPLOAD_DOCUMENT, uploadDocument),
    takeLatest(actionTypes.SUBMIT_LISTING_OFFER, submitListingOffer),
  ]);
}
